/**
 * abort-signal-compat — iOS/Safari compatibility helpers for
 * `build-real-checkin-context.ts`'s per-item deadline (third adversarial
 * review finding H2), added as a follow-up once the coordinator flagged
 * that `AbortSignal.any` (Safari 17.4/iOS 17.4+) and `AbortSignal.timeout`
 * (Safari 16.0/iOS 16.0+) are both too recent to assume — valets may run
 * older iPhones, and this offline feature exists specifically for
 * lower-connectivity field use where an older device is MORE likely, not
 * less.
 *
 * Both helpers feature-detect the native static method at CALL time (never
 * cached at module load), so:
 *   1. On a real modern browser, this is a one-line passthrough — zero
 *      behavior change, zero overhead beyond the `typeof` check.
 *   2. On an old iOS/Safari runtime missing the method, the manual fallback
 *      kicks in transparently.
 *   3. Tests can delete/restore the native static method per-test to
 *      exercise both paths without needing an actual old browser.
 */

/**
 * timeoutSignal(ms) — equivalent to `AbortSignal.timeout(ms)`. Falls back to
 * a plain `AbortController` + `setTimeout` for Safari/iOS < 16.0. The manual
 * timer is `unref()`'d when running under Node (test environments) so it
 * never keeps a process alive — a no-op in real browsers, which have no
 * `unref` on their timer handles.
 */
export function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(new DOMException('The operation timed out.', 'TimeoutError'));
  }, ms);
  if (typeof timer === 'object' && timer !== null && 'unref' in timer) {
    (timer as unknown as { unref: () => void }).unref();
  }
  controller.signal.addEventListener('abort', () => clearTimeout(timer), { once: true });

  return controller.signal;
}

/**
 * anySignal(signals) — equivalent to `AbortSignal.any(signals)`: resolves to
 * a single signal that aborts the instant ANY of the given signals aborts,
 * propagating that signal's own `reason`. Falls back to a manual
 * `AbortController` + per-signal `abort` listeners (cleaned up the instant
 * one fires, so the others never leak a listener) for Safari/iOS < 17.4. If
 * a signal is ALREADY aborted at call time, aborts immediately — matching
 * the native `AbortSignal.any` contract.
 */
export function anySignal(signals: AbortSignal[]): AbortSignal {
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any(signals);
  }

  const controller = new AbortController();

  const alreadyAborted = signals.find((s) => s.aborted);
  if (alreadyAborted) {
    controller.abort(alreadyAborted.reason);
    return controller.signal;
  }

  const cleanupFns: Array<() => void> = [];
  const cleanup = () => {
    for (const fn of cleanupFns) fn();
  };

  for (const signal of signals) {
    const onAbort = () => {
      controller.abort(signal.reason);
      cleanup();
    };
    signal.addEventListener('abort', onAbort, { once: true });
    cleanupFns.push(() => signal.removeEventListener('abort', onAbort));
  }

  return controller.signal;
}
